import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../assets/css/systemManagement.css';
import { BranchesModel } from '../models/BranchesModel';
import {
    FilterDataModel,
    SortModel,
    SortDirection,
    ActivityStatus,
    BranchModel,
    UserModel,
} from '../models/superbit-api-models';
import Pagger from '../components/_Pagger';
import DeleteAlert from '../components/DeleteAlert';
import NewBranch from './_NewBranch';

const PAGE_SIZE = 50; // server-side page size (Constants.NUMBER_OF_OBJECTS_PER_PAGE)
const INDENT_PX = 22; // per-depth indentation of the name cell

// Sortable columns — `sortBy` matches the property names the old MVC view sent
// (data-sort-by) so the existing server-side sorting keeps working.
type Column = { key: string; label: string; sortBy?: string };
const COLUMNS: Column[] = [
    { key: 'name', label: 'שם', sortBy: 'Name' },
    { key: 'manager', label: 'מנהל' },
    { key: 'address', label: 'כתובת', sortBy: 'Address' },
    { key: 'status', label: 'סטטוס', sortBy: 'Status' },
];

const isActive = (s?: ActivityStatus) => s === ActivityStatus._0; // 0 = Active (פעיל)

const BranchesManagement = () => {
    const [branchesModel] = useState<BranchesModel>(() => new BranchesModel());

    const [branches, setBranches] = useState<BranchModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [sortProp, setSortProp] = useState<string | undefined>(undefined);
    const [sortDir, setSortDir] = useState<SortDirection>(SortDirection._0);

    const [page, setPage] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [reloadTick, setReloadTick] = useState<number>(0); // bumped to force a reload (e.g. on refresh/clear)

    // Expanded branch ids (collapsed by default, like the old plus/minus tree).
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined);
    const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

    // Add/edit modal. editBranch set = edit; addParentId set = add department.
    const [showModal, setShowModal] = useState<boolean>(false);
    const [editBranch, setEditBranch] = useState<BranchModel | undefined>(undefined);
    const [addParentId, setAddParentId] = useState<string | undefined>(undefined);

    const buildFilter = (): FilterDataModel => {
        const filter = new FilterDataModel();
        filter.searchTerm = searchTerm ? searchTerm : undefined;
        filter.page = page;
        if (sortProp) {
            const sort = new SortModel();
            sort.property = sortProp;
            sort.sortDirection = sortDir;
            filter.sortBy = [sort];
        }
        return filter;
    };

    const load = async () => {
        setLoading(true);
        try {
            const result = await branchesModel.branchSort(buildFilter());
            const list = result && result.branches ? result.branches : [];
            setBranches(list);
            setTotalItems(result && result.allRowsCounter ? result.allRowsCounter : 0);
            // When searching, auto-expand everything so matching departments/users
            // are visible (the server already returned the filtered sub-tree).
            if (searchTerm) {
                setExpanded(new Set(list.map((b) => b.id).filter((id): id is string => !!id)));
            }
        } catch (e) {
            console.error('Failed to load branches', e);
            setBranches([]);
            setTotalItems(0);
        } finally {
            setLoading(false);
        }
    };

    // Reload whenever sort or page change (reloadTick forces a reload).
    useEffect(() => {
        load();
    }, [sortProp, sortDir, page, reloadTick]);

    // Group children by parentId once per data change (roots keyed under '').
    const childrenByParent = useMemo(() => {
        const map = new Map<string, BranchModel[]>();
        branches.forEach((b) => {
            const key = b.parentId || '';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(b);
        });
        return map;
    }, [branches]);

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') applySearch();
    };

    const applySearch = () => {
        if (page !== 1) setPage(1); // page change triggers reload
        else load();
    };

    // Refresh button: clear filters and reload (reloadTick guarantees the reload).
    const clearFilters = () => {
        setSearchTerm('');
        setPage(1);
        setReloadTick((t) => t + 1);
    };

    // Auto-search: reload shortly after the user stops typing (skip the initial mount).
    const searchDidMount = useRef(false);
    useEffect(() => {
        if (!searchDidMount.current) {
            searchDidMount.current = true;
            return;
        }
        const t = setTimeout(() => applySearch(), 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const onSort = (column: Column) => {
        if (!column.sortBy) return;
        if (sortProp === column.sortBy) {
            setSortDir(sortDir === SortDirection._0 ? SortDirection._1 : SortDirection._0);
        } else {
            setSortProp(column.sortBy);
            setSortDir(SortDirection._0);
        }
    };

    const toggleExpand = (id?: string) => {
        if (!id) return;
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleMenu = (id?: string) => {
        if (!id) return;
        setOpenMenuId(openMenuId === id ? undefined : id);
    };

    const onAddBranch = () => {
        setEditBranch(undefined);
        setAddParentId(undefined);
        setShowModal(true);
    };

    const onAddDepartment = (branch: BranchModel) => {
        setOpenMenuId(undefined);
        setEditBranch(undefined);
        setAddParentId(branch.id);
        setShowModal(true);
    };

    const onEdit = (branch: BranchModel) => {
        setOpenMenuId(undefined);
        setEditBranch(branch);
        setAddParentId(undefined);
        setShowModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await branchesModel.branchDelete(deleteId);
            await load();
        } catch (e) {
            console.error('Failed to delete branch', e);
        } finally {
            setDeleteId(undefined);
        }
    };

    const hasChildren = (branch: BranchModel): boolean =>
        (!!branch.id && (childrenByParent.get(branch.id)?.length || 0) > 0) ||
        (!!branch.usersInBranch && branch.usersInBranch.length > 0);

    // Flatten the tree into ordered table rows, respecting expand/collapse state.
    const renderBranchRows = (parentId: string, depth: number): React.ReactNode[] => {
        const rows: React.ReactNode[] = [];
        const children = childrenByParent.get(parentId) || [];

        children.forEach((branch) => {
            const expandable = hasChildren(branch);
            const isOpen = !!branch.id && expanded.has(branch.id);

            rows.push(
                <tr key={branch.id}>
                    <td className="bold branch-name-cell">
                        <span style={{ paddingRight: depth * INDENT_PX }} className="branch-indent" />
                        {expandable ? (
                            <span
                                className={`branch-toggle pointer ${isOpen ? 'open' : ''}`}
                                onClick={() => toggleExpand(branch.id)}
                            />
                        ) : (
                            <span className="branch-toggle branch-toggle--leaf" />
                        )}
                        <span>{branch.name}</span>
                    </td>
                    <td>{branch.branchManager ? branch.branchManager.fullName : ''}</td>
                    <td>{branch.address}</td>
                    <td className={isActive(branch.status) ? 'online' : 'offline'}>
                        <span>{isActive(branch.status) ? 'פעיל' : 'לא פעיל'}</span>
                        <span className="dot" />
                    </td>
                    <td className="users-actions">
                        <button
                            type="button"
                            className="actionBtn pointer"
                            onClick={() => toggleMenu(branch.id)}
                        >
                            <div className="edit-icon" />
                            <span>פעולות</span>
                            <div className="dropdown-icon" />
                        </button>
                        {openMenuId === branch.id && (
                            <ul className="actionBtnMenu">
                                <li className="actionBtnMenuItem" onClick={() => onEdit(branch)}>
                                    ערוך
                                </li>
                                <li className="actionBtnMenuItem" onClick={() => onAddDepartment(branch)}>
                                    הוסף מחלקה
                                </li>
                                <li
                                    className="actionBtnMenuItem"
                                    onClick={() => {
                                        setOpenMenuId(undefined);
                                        setDeleteId(branch.id);
                                    }}
                                >
                                    מחק
                                </li>
                            </ul>
                        )}
                    </td>
                </tr>
            );

            if (expandable && isOpen) {
                // Child departments first, then the users that belong to this branch.
                if (branch.id) rows.push(...renderBranchRows(branch.id, depth + 1));
                (branch.usersInBranch || []).forEach((user) => {
                    rows.push(renderUserRow(user, depth + 1));
                });
            }
        });

        return rows;
    };

    const renderUserRow = (user: UserModel, depth: number): React.ReactNode => (
        <tr key={`u_${user.id}`} className="branch-user-row">
            <td className="branch-name-cell">
                <span style={{ paddingRight: depth * INDENT_PX }} className="branch-indent" />
                <span className="branch-toggle branch-toggle--leaf" />
                <span className="branch-user-icon" />
                <span>{user.fullName}</span>
            </td>
            <td />
            <td />
            <td className={isActive(user.status) ? 'online' : 'offline'}>
                <span>{isActive(user.status) ? 'פעיל' : 'לא פעיל'}</span>
                <span className="dot" />
            </td>
            <td />
        </tr>
    );

    const pagerConf = {
        pageSize: PAGE_SIZE,
        currentPage: page,
        totalItems: totalItems,
    };

    return (
        <div className="users-management rtl">
            <div className="users-toolbar flex-centered">
                <div className="users-search">
                    <span className="search-icon" />
                    <input
                        type="text"
                        className="users-search__input"
                        placeholder="חיפוש"
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={onSearchKeyDown}
                    />
                </div>
                <div className="users-refresh pointer" title="רענון" onClick={() => clearFilters()} />
            </div>

            <div className="users-table-wrapper">
                <table className="users-table">
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={`users-th ${col.sortBy ? 'sortable pointer' : ''} ${
                                        sortProp === col.sortBy ? 'sorted' : ''
                                    }`}
                                    onClick={() => onSort(col)}
                                >
                                    <span>{col.label}</span>
                                    {col.sortBy && (
                                        <span
                                            className={`arrow-updown ${
                                                sortProp === col.sortBy
                                                    ? sortDir === SortDirection._0
                                                        ? 'asc'
                                                        : 'desc'
                                                    : ''
                                            }`}
                                        />
                                    )}
                                </th>
                            ))}
                            <th className="users-th users-th--add">
                                <div className="add-btn pointer" onClick={onAddBranch}>
                                    <span className="add-file" />
                                    <span className="text">הוסף סניף</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderBranchRows('', 0)}
                        {!loading && branches.length === 0 && (
                            <tr>
                                <td className="users-empty" colSpan={COLUMNS.length + 1}>
                                    לא נמצאו סניפים
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalItems > 0 && (
                <div className="users-pager">
                    <Pagger pagerConf={pagerConf} changePage={(p: number) => setPage(p)} refresh={() => load()} />
                </div>
            )}

            <DeleteAlert
                show={!!deleteId}
                onClose={() => setDeleteId(undefined)}
                onConfirm={confirmDelete}
            />

            <NewBranch
                show={showModal}
                branch={editBranch}
                parentId={addParentId}
                onClose={() => setShowModal(false)}
                onSaved={() => {
                    setShowModal(false);
                    load();
                }}
            />
        </div>
    );
};

export default BranchesManagement;
export {};
